import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import moment from "moment-timezone";
import PhoneField from "./PhoneField";
import Stepper, {
  StepperRule,
  StepperRuleGroup,
} from "./NumberStepper";
import {
  fetchEstimatedTimeCustomersSpend,
  fetchPlaceOfTable,
  fetchRestaurantDetails,
  fetchRestaurantOpenDays,
  fetchTables,
  getRestaurantNotificationsList
} from "../actions/restaurantActions";
import {
  createDefaultRestaurantSetupDraft,
  saveRestaurantSetupTourDraft,
  startRestaurantReservationTour,
  updateRestaurantSetupTourPage
} from "../utils/restaurantSetupTour";
import {
  getSetupApiErrorMessage,
  restaurantSetupApi
} from "../utils/restaurantSetupApi";
import {
  hasAgilFeatures,
  hasCompletoFeatures,
  planTier
} from "../utils/planFeatures";

const MAX_ROOMS = 20;
const MAX_TABLES_PER_ROOM = 100;
const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(31, 25, 26, 0.7);
  backdrop-filter: blur(3px);

  @media (max-width: 620px) {
    align-items: stretch;
    padding: 0;
  }
`;

const Modal = styled.div`
  width: min(820px, calc(100vw - 40px));
  height: min(760px, calc(100vh - 40px));
  height: min(760px, calc(100dvh - 40px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 18px;
  border: 1px solid #eaded9;
  box-shadow: 0 24px 80px rgba(33, 25, 26, 0.35);
  font-family: "Poppins", sans-serif;

  @media (max-width: 620px) {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }
`;

const Header = styled.div`
  flex: 0 0 auto;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #eee5e1;
  background: #fffaf7;
`;

const Eyebrow = styled.div`
  margin-bottom: 9px;
  color: #ee754c;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Progress = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
`;

const ProgressItem = styled.div`
  min-width: 0;
  padding: 7px 8px;
  border-radius: 8px;
  background: ${({ $active, $done }) =>
    $active ? "#ee754c" : $done ? "#f8d8ca" : "#efeceb"};
  color: ${({ $active }) => ($active ? "#fff" : "#6c6162")};
  font-size: 0.72rem;
  font-weight: 750;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  @media (max-width: 620px) {
    padding: 20px 18px;
  }
`;

const Title = styled.h2`
  margin: 0 0 9px;
  color: var(--app-orange, #ee754c);
  font-size: clamp(1.2rem, 3vw, 1.65rem);
  font-weight: 850;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  margin: 0 0 22px;
  color: #716567;
  font-size: 0.93rem;
  line-height: 1.55;
`;

const Footer = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 14px 22px 18px;
  border-top: 1px solid #eee5e1;
  background: #fff;

  @media (max-width: 620px) {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  }
`;

const Button = styled.button`
  min-height: 40px;
  padding: 9px 15px;
  border: 0;
  border-radius: 9px;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.48;
    cursor: default;
  }
`;

const BackButton = styled(Button)`
  background: #eef2f1;
  color: #586966;
`;

const NextButton = styled(Button)`
  margin-left: auto;
  background: #ee754c;
  color: white;
`;

const TextButton = styled(Button)`
  background: transparent;
  color: #8a7f81;
`;

const AddScheduleButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f4;
  color: #526663;
`;

const AddIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  color: #ee754c;
  font-size: 1.1rem;
  font-weight: 900;
  line-height: 1;
`;

const Counter = styled.div`
  display: inline-grid;
  grid-template-columns: 42px minmax(65px, auto) 42px;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid #d9d2cf;
  border-radius: 11px;
  background: #fff;
`;

const CounterButton = styled.button`
  border: 0;
  background: #f4f1ef;
  color: #ee754c;
  font-size: 1.35rem;
  font-weight: 850;
  cursor: pointer;

  &:disabled {
    color: #c9c1bf;
    cursor: default;
  }
`;

const CounterValueDisplay = styled.div`
  min-width: 92px;
  padding: 9px 8px;
  color: #49383b;
  font-size: 0.94rem;
  font-weight: 800;
  text-align: center;
  white-space: nowrap;
`;

const Input = styled.input`
  width: 100%;
  min-height: 42px;
  padding: 9px 11px;
  border: 1px solid ${({ $invalid }) => ($invalid ? "#d9534f" : "#d9d2cf")};
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: #49383b;
  font-size: 0.92rem;

  &:focus {
    border-color: #ee754c;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.13);
  }
`;

const Select = styled.select`
  width: 100%;
  min-height: 42px;
  padding: 9px 11px;
  border: 1px solid #d9d2cf;
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: #49383b;
  font-size: 0.9rem;

  &:focus {
    border-color: #ee754c;
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 7px;
  color: #54484a;
  font-size: 0.84rem;
  font-weight: 800;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 2 }) => $columns}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const RoomNameRow = styled.div`
  display: grid;
  grid-template-columns: 92px 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const RoomLabel = styled.div`
  color: #7a6e70;
  font-size: 0.82rem;
  font-weight: 750;
`;

const TablesScroll = styled.div`
  max-height: 290px;
  margin-top: 14px;
  padding-right: 4px;
  overflow-y: auto;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  margin-bottom: 7px;
  border: 1px solid #ece5e2;
  border-radius: 10px;
  background: #fcfaf9;
`;

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 7px;

  @media (max-width: 680px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Weekday = styled.button`
  min-height: 42px;
  padding: 7px 5px;
  border: 1px solid ${({ $selected }) => ($selected ? "#ee754c" : "#ddd5d2")};
  border-radius: 9px;
  background: ${({ $selected }) => ($selected ? "#fff0e9" : "#fff")};
  color: ${({ $selected }) => ($selected ? "#d9603b" : "#675c5e")};
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
`;

const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Choice = styled.button`
  min-height: 64px;
  padding: 10px;
  border: 2px solid ${({ $selected }) => ($selected ? "#ee754c" : "#e7dfdc")};
  border-radius: 11px;
  background: ${({ $selected }) => ($selected ? "#fff3ed" : "#fff")};
  color: #514547;
  font-size: 0.92rem;
  font-weight: 800;
  cursor: pointer;
`;

const SummaryList = styled.div`
  display: grid;
  gap: 9px;
  margin-bottom: 18px;
`;

const SummaryCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 11px 13px;
  border: 1px solid #e7dfdc;
  border-radius: 10px;
  background: #fcfaf9;
  color: #605456;
  font-size: 0.86rem;
`;

const DeleteScheduleButton = styled.button`
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: #fff0ef;
  color: #ba4742;
  font-size: 1.15rem;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    background: #ffe4e1;
  }
`;

const ScheduleBuilder = styled.div`
  display: grid;
  gap: 14px;
  padding: 14px;
  margin-bottom: 18px;
  border: 1px solid #e7dfdc;
  border-radius: 12px;
  background: #fcfaf9;
`;

const AverageStayBuilderRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const AverageStayBuilderField = styled.div`
  display: grid;
  gap: 8px;
`;

const AverageStayRule = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px;
  border: 1px solid #ece5e2;
  border-radius: 11px;
  background: #fcfaf9;
  color: #5f5456;
  font-size: 0.88rem;
  font-weight: 700;
`;

const NotificationSection = styled.div`
  padding: 14px;
  margin-bottom: 14px;
  border: 1px solid #ece5e2;
  border-radius: 12px;
  background: #fcfaf9;
`;

const NotificationTitle = styled.h3`
  margin: 0 0 7px;
  color: #49383b;
  font-size: 1rem;
  font-weight: 850;
`;

const NotificationDescription = styled.p`
  margin: 0 0 12px;
  color: #716567;
  font-size: 0.84rem;
  line-height: 1.5;
`;

const Toggle = styled.button`
  width: 58px;
  height: 31px;
  padding: 3px;
  border: 0;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? "#ee754c" : "#cbc5c3")};
  cursor: pointer;
  transition: background 0.15s ease;

  &::after {
    content: "";
    display: block;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background: white;
    transform: translateX(${({ $on }) => ($on ? "27px" : "0")});
    transition: transform 0.15s ease;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px;
  border: 1px solid #ece5e2;
  border-radius: 11px;
  background: #fcfaf9;
`;

const ErrorBox = styled.div`
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 9px;
  background: #fff0ef;
  color: #a43d3a;
  font-size: 0.84rem;
  line-height: 1.45;
`;

const InfoBox = styled.div`
  margin-top: 14px;
  padding: 11px 13px;
  border-radius: 9px;
  background: #f2f6f5;
  color: #586765;
  font-size: 0.84rem;
  line-height: 1.5;
`;

const DefinitionValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DefinitionUnit = styled.span`
  color: #615557;
  font-size: 0.92rem;
  font-weight: 800;
`;

const SavingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 270px;
  text-align: center;
`;

const Spinner = styled.div`
  width: 42px;
  height: 42px;
  margin-bottom: 17px;
  border: 4px solid #f0dfd7;
  border-top-color: #ee754c;
  border-radius: 50%;
  animation: setup-spin 0.75s linear infinite;

  @keyframes setup-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const DurationStepper = ({ value, onChange, t, min = 15, max = 600, step = 15 }) => {
  const normalized = Math.min(max, Math.max(min, Number(value) || min));
  const set = next => onChange(Math.min(max, Math.max(min, Number(next) || min)));

  return (
    <Counter>
      <CounterButton
        type="button"
        onClick={() => set(normalized - step)}
        disabled={normalized <= min}
        aria-label="-"
      >
        −
      </CounterButton>
      <CounterValueDisplay>{durationLabel(normalized, t)}</CounterValueDisplay>
      <CounterButton
        type="button"
        onClick={() => set(normalized + step)}
        disabled={normalized >= max}
        aria-label="+"
      >
        +
      </CounterButton>
    </Counter>
  );
};

const durationLabel = (minutes, t) => {
  const value = Number(minutes) || 0;
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  if (!hours) return t("ui.restaurantSetupWizard.minutesValue", { minutes: mins });
  if (!mins) return t("ui.restaurantSetupWizard.hoursValue", { hours });
  return t("ui.restaurantSetupWizard.hoursMinutesValue", { hours, minutes: mins });
};

const intervalMinutes = value => {
  const [hours = "0", minutes = "0"] = String(value || "00:00").split(":");
  return Number(hours) * 60 + Number(minutes);
};

const ensureRoomShape = (room, index) => ({
  name: room?.name ?? "",
  defaultSeats: Math.max(1, Number(room?.defaultSeats) || 2),
  tables:
    Array.isArray(room?.tables) && room.tables.length
      ? room.tables.map(table => ({ seats: Math.max(1, Number(table?.seats) || 2) }))
      : [{ seats: 2 }],
  _key: room?._key || `room-${index}-${Date.now()}`
});

const definitionKeys = [
  "limitPeopleReservation",
  "autoAccept",
  "babyChairs",
  "timezone"
];

const RestaurantSetupWizard = ({ tour }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state.userLogin?.userInfo);
  const restaurantId = tour.restaurantId;
  const token = userInfo?.token;
  const [draft, setDraft] = useState(() => {
    const defaults = createDefaultRestaurantSetupDraft();
    const stored = tour.draft || {};
    const storedSchedules = Array.isArray(stored.schedules) ? stored.schedules : [];

    return {
      ...defaults,
      ...stored,
      schedules: storedSchedules.filter(
        schedule =>
          schedule?.days?.length &&
          schedule?.startTime &&
          schedule?.endTime &&
          schedule.startTime < schedule.endTime
      ),
      scheduleDraft: {
        ...defaults.scheduleDraft,
        ...(stored.scheduleDraft || {})
      },
      averageStayRules: Array.isArray(stored.averageStayRules)
        ? stored.averageStayRules
            .map(rule => ({
              people: Math.min(100, Math.max(1, Number(rule?.people) || 1)),
              minutes: Math.min(600, Math.max(15, Number(rule?.minutes) || 90))
            }))
            .filter(
              (rule, index, rules) =>
                rules.findIndex(item => item.people === rule.people) === index
            )
        : defaults.averageStayRules,
      averageStayDraft: {
        ...defaults.averageStayDraft,
        ...(stored.averageStayDraft || {})
      },
      definitions: {
        ...defaults.definitions,
        ...(stored.definitions || {})
      },
      phoneNotifications: {
        ...defaults.phoneNotifications,
        ...(stored.phoneNotifications || {})
      },
      emailNotifications: {
        ...defaults.emailNotifications,
        ...(stored.emailNotifications || {})
      }
    };
  });
  const [pageKey, setPageKey] = useState(tour.pageKey || "roomCount");
  const [restaurant, setRestaurant] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const timezones = useMemo(() => moment.tz.names(), []);

  useEffect(() => {
    setPageKey(tour.pageKey || "roomCount");
  }, [tour.pageKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      saveRestaurantSetupTourDraft(restaurantId, draft);
    }, 100);
    return () => window.clearTimeout(timer);
  }, [draft, restaurantId]);

  useEffect(() => {
    let active = true;
    const loadRestaurant = async () => {
      try {
        const data = await restaurantSetupApi.getRestaurant(restaurantId, token);
        if (!active) return;
        setRestaurant(data);
        setDraft(current => {
          const defaults = createDefaultRestaurantSetupDraft();
          const currentDefs = current.definitions || defaults.definitions;
          const detectedTimezone =
            data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Lisbon";
          const parseDurationHours = value => {
            if (typeof value === "number") return value;
            const hours = Number(String(value || "0").split(":")[0]);
            return Number.isFinite(hours) ? hours : 0;
          };

          return {
            ...current,
            definitions: {
              ...defaults.definitions,
              ...currentDefs,
              limitPeopleReservation:
                currentDefs.limitPeopleReservation ?? data?.limit_people_reservation ?? 8,
              autoAcceptLimit:
                currentDefs.autoAcceptLimit ?? data?.auto_accept_limit ?? 4,
              autoAcceptMinHours:
                currentDefs.autoAcceptMinHours ??
                parseDurationHours(data?.only_auto_accept_before_this_time),
              autoAcceptFutureDays:
                currentDefs.autoAcceptFutureDays ??
                Math.max(
                  0,
                  Math.round(
                    Number(
                      currentDefs.autoAcceptFutureHours ??
                        data?.only_auto_accept_before_this_time_future ??
                        720
                    ) / 24
                  )
                ),
              babyChairs:
                currentDefs.babyChairs ?? Number(data?.number_of_babychairs || 0),
              babyChairsPerReservation:
                currentDefs.babyChairsPerReservation ??
                Number(data?.limit_baby_chairs_reservation || 0),
              timezone: currentDefs.timezone || detectedTimezone
            }
          };
        });
      } catch (loadError) {
        if (active) setError(getSetupApiErrorMessage(loadError));
      }
    };
    loadRestaurant();
    return () => {
      active = false;
    };
  }, [restaurantId, token]);

  useEffect(() => {
    const preventEscape = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", preventEscape, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", preventEscape, true);
    };
  }, []);

  const updateDraft = updater => {
    setError("");
    setDraft(current =>
      typeof updater === "function" ? updater(current) : { ...current, ...updater }
    );
  };

  const go = key => {
    setError("");
    setPageKey(key);
    updateRestaurantSetupTourPage(restaurantId, key);
  };

  useEffect(() => {
    const [storedType, rawIndex] = String(pageKey).split(":");
    const storedIndex = Number(rawIndex);
    let migratedPage = null;

    if (["scheduleDays", "scheduleTime", "moreSchedules"].includes(storedType)) {
      migratedPage = "schedule";
    } else if (["phone", "email"].includes(storedType)) {
      migratedPage = "notifications";
    } else if (storedType === "definition") {
      if (!Number.isInteger(storedIndex) || storedIndex < 0) {
        migratedPage = "definition:0";
      } else if (storedIndex >= definitionKeys.length) {
        migratedPage = `definition:${definitionKeys.length - 1}`;
      }
    }

    if (migratedPage && migratedPage !== pageKey) {
      setPageKey(migratedPage);
      updateRestaurantSetupTourPage(restaurantId, migratedPage);
    }
  }, [pageKey, restaurantId]);

  const setRoomCount = count => {
    const safeCount = Math.min(MAX_ROOMS, Math.max(1, Number(count) || 1));
    updateDraft(current => {
      const rooms = Array.from({ length: safeCount }, (_, index) =>
        ensureRoomShape(current.rooms?.[index], index)
      );
      return { ...current, roomCount: safeCount, rooms };
    });
  };

  const setRoomName = (index, value) => {
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, roomIndex) =>
        roomIndex === index ? { ...room, name: value } : room
      )
    }));
  };

  const setRoomTablesCount = (roomIndex, count) => {
    const safeCount = Math.min(
      MAX_TABLES_PER_ROOM,
      Math.max(1, Number(count) || 1)
    );
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, index) => {
        if (index !== roomIndex) return room;
        const tables = Array.from({ length: safeCount }, (_, tableIndex) => ({
          seats: Math.max(
            1,
            Number(room.tables?.[tableIndex]?.seats) || Number(room.defaultSeats) || 2
          )
        }));
        return { ...room, tables };
      })
    }));
  };

  const setRoomDefaultSeats = (roomIndex, seats) => {
    const safeSeats = Math.max(1, Math.min(100, Number(seats) || 1));
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, index) =>
        index === roomIndex
          ? {
              ...room,
              defaultSeats: safeSeats,
              tables: room.tables.map(() => ({ seats: safeSeats }))
            }
          : room
      )
    }));
  };

  const setTableSeats = (roomIndex, tableIndex, seats) => {
    const safeSeats = Math.max(1, Math.min(100, Number(seats) || 1));
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, index) =>
        index === roomIndex
          ? {
              ...room,
              tables: room.tables.map((table, currentTableIndex) =>
                currentTableIndex === tableIndex ? { ...table, seats: safeSeats } : table
              )
            }
          : room
      )
    }));
  };

  const updateScheduleDraft = patch => {
    updateDraft(current => ({
      ...current,
      scheduleDraft: {
        ...(current.scheduleDraft || createDefaultRestaurantSetupDraft().scheduleDraft),
        ...patch
      }
    }));
  };

  const toggleScheduleDraftDay = day => {
    const schedule = draft.scheduleDraft || createDefaultRestaurantSetupDraft().scheduleDraft;
    const days = schedule.days.includes(day)
      ? schedule.days.filter(item => item !== day)
      : [...schedule.days, day];
    updateScheduleDraft({ days });
  };

  const saveScheduleRow = () => {
    const schedule = draft.scheduleDraft || createDefaultRestaurantSetupDraft().scheduleDraft;
    if (!schedule.days?.length) {
      setError(t("ui.restaurantSetupWizard.errors.weekday"));
      return;
    }
    if (!schedule.startTime || !schedule.endTime || schedule.startTime >= schedule.endTime) {
      setError(t("ui.restaurantSetupWizard.errors.scheduleTime"));
      return;
    }

    updateDraft(current => ({
      ...current,
      schedules: [
        ...(current.schedules || []),
        {
          days: [...schedule.days],
          startTime: schedule.startTime,
          endTime: schedule.endTime
        }
      ],
      scheduleDraft: {
        days: [],
        startTime: schedule.startTime,
        endTime: schedule.endTime
      }
    }));
  };

  const deleteScheduleRow = scheduleIndex => {
    updateDraft(current => ({
      ...current,
      schedules: current.schedules.filter((_, index) => index !== scheduleIndex)
    }));
  };

  const updateAverageStayDraft = patch => {
    updateDraft(current => ({
      ...current,
      averageStayDraft: {
        ...(current.averageStayDraft || createDefaultRestaurantSetupDraft().averageStayDraft),
        ...patch
      }
    }));
  };

  const saveAverageStayRule = () => {
    const currentRule =
      draft.averageStayDraft || createDefaultRestaurantSetupDraft().averageStayDraft;
    const people = Math.min(100, Math.max(1, Number(currentRule.people) || 1));
    const minutes = Math.min(600, Math.max(15, Number(currentRule.minutes) || 90));

    updateDraft(current => {
      const withoutSamePartySize = (current.averageStayRules || []).filter(
        rule => Number(rule.people) !== people
      );
      return {
        ...current,
        averageStayRules: [...withoutSamePartySize, { people, minutes }].sort(
          (a, b) => Number(a.people) - Number(b.people)
        ),
        averageStayDraft: { people, minutes }
      };
    });
  };

  const deleteAverageStayRule = people => {
    updateDraft(current => ({
      ...current,
      averageStayRules: (current.averageStayRules || []).filter(
        rule => Number(rule.people) !== Number(people)
      )
    }));
  };

  const updateDefinition = (key, value) => {
    updateDraft(current => ({
      ...current,
      definitions: {
        ...current.definitions,
        [key]: value
      }
    }));
  };

  const parsePage = () => {
    const [type, rawIndex] = String(pageKey).split(":");
    return { type, index: Number(rawIndex) || 0 };
  };

  const { type: pageType, index: pageIndex } = parsePage();

  const roomNamesValid = () => {
    const names = draft.rooms.map(room => String(room.name || "").trim());
    return (
      names.every(Boolean) &&
      new Set(names.map(name => name.toLocaleLowerCase())).size === names.length
    );
  };

  const emailValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ""));

  const currentDefinitionIndex = pageType === "definition" ? pageIndex : -1;
  const currentDefinitionKey = definitionKeys[currentDefinitionIndex];
  // Tier 1 is Essencial on either cadence (package 1 monthly, 4 yearly).
  const isLegacyDefinitionBlocked =
    Boolean(restaurant) && planTier(restaurant?.sub_lvl) <= 1;
  const definitionIsPlanBlocked = [
    "limitPeopleReservation",
    "autoAccept"
  ].includes(currentDefinitionKey)
    ? Boolean(restaurant) && !hasAgilFeatures(restaurant)
    : currentDefinitionKey === "babyChairs"
      ? isLegacyDefinitionBlocked
      : false;
  const slotCapacityIsPlanBlocked =
    Boolean(restaurant) && !hasCompletoFeatures(restaurant);

  const sectionIndex = (() => {
    if (["roomCount", "roomNames", "roomTables"].includes(pageType)) return 0;
    if (
      [
        "schedule",
        "slotInterval",
        "slotCapacity",
        "averageStay"
      ].includes(pageType)
    )
      return 1;
    if (pageType === "definition") return 2;
    return 3;
  })();

  const validateCurrent = () => {
    if (pageType === "roomCount") return Number(draft.roomCount) > 0;
    if (pageType === "roomNames") {
      if (!roomNamesValid()) {
        setError(t("ui.restaurantSetupWizard.errors.roomNames"));
        return false;
      }
    }
    if (pageType === "roomTables") {
      const room = draft.rooms[pageIndex];
      if (!room?.tables?.length || room.tables.some(table => Number(table.seats) <= 0)) {
        setError(t("ui.restaurantSetupWizard.errors.tables"));
        return false;
      }
    }
    if (pageType === "schedule" && !draft.schedules?.length) {
      setError(t("ui.restaurantSetupWizard.errors.scheduleRequired"));
      return false;
    }
    if (
      pageType === "slotCapacity" &&
      !slotCapacityIsPlanBlocked &&
      Number(draft.slotCapacity) <= 0
    ) {
      setError(t("ui.restaurantSetupWizard.errors.capacity"));
      return false;
    }
    if (pageType === "averageStay" && !draft.averageStayRules?.length) {
      setError(t("ui.restaurantSetupWizard.errors.averageStayRequired"));
      return false;
    }
    if (pageType === "notifications") {
      if (draft.phoneNotifications.enabled && !isPhoneValid) {
        setError(t("ui.restaurantSetupWizard.errors.phone"));
        return false;
      }
      if (draft.emailNotifications.enabled && !emailValid(draft.emailNotifications.email)) {
        setError(t("ui.restaurantSetupWizard.errors.email"));
        return false;
      }
    }
    return true;
  };

  const getNextPage = () => {
    if (pageType === "roomCount") return "roomNames";
    if (pageType === "roomNames") return "roomTables:0";
    if (pageType === "roomTables") {
      return pageIndex < draft.rooms.length - 1
        ? `roomTables:${pageIndex + 1}`
        : "schedule";
    }
    if (pageType === "schedule") return "slotInterval";
    if (pageType === "slotInterval") return "slotCapacity";
    if (pageType === "slotCapacity") return "averageStay";
    if (pageType === "averageStay") return "definition:0";
    if (pageType === "definition") {
      return pageIndex < definitionKeys.length - 1
        ? `definition:${pageIndex + 1}`
        : "notifications";
    }
    return null;
  };

  const getPreviousPage = () => {
    if (pageType === "roomCount") return null;
    if (pageType === "roomNames") return "roomCount";
    if (pageType === "roomTables") {
      return pageIndex > 0 ? `roomTables:${pageIndex - 1}` : "roomNames";
    }
    if (pageType === "schedule") {
      return `roomTables:${Math.max(0, draft.rooms.length - 1)}`;
    }
    if (pageType === "slotInterval") return "schedule";
    if (pageType === "slotCapacity") return "slotInterval";
    if (pageType === "averageStay") return "slotCapacity";
    if (pageType === "definition") {
      return pageIndex > 0 ? `definition:${pageIndex - 1}` : "averageStay";
    }
    if (pageType === "notifications") return `definition:${definitionKeys.length - 1}`;
    return null;
  };

  const handleNext = () => {
    setError("");
    if (!validateCurrent()) return;
    const next = getNextPage();
    if (next) go(next);
  };

  const handleBack = () => {
    const previous = getPreviousPage();
    if (previous) go(previous);
  };

  const saveAll = async (draftOverride = null, skipCurrentValidation = false) => {
    const effectiveDraft = draftOverride || draft;
    setError("");
    if (!skipCurrentValidation && !validateCurrent()) return;
    setIsSaving(true);

    try {
      const latestRestaurant = await restaurantSetupApi.getRestaurant(restaurantId, token);
      const roomNames = effectiveDraft.rooms.map(room => room.name.trim());
      const blocked = !hasAgilFeatures(latestRestaurant);
      const legacyDefinitionsBlocked = planTier(latestRestaurant?.sub_lvl) <= 1;
      const intervalBlocked = !hasCompletoFeatures(latestRestaurant);
      await restaurantSetupApi.ensureRoomsAndTables(restaurantId, effectiveDraft.rooms, token);
      await restaurantSetupApi.ensureSchedules(
        restaurantId,
        effectiveDraft.schedules,
        {
          slotInterval: effectiveDraft.slotInterval,
          slotCapacity: effectiveDraft.slotCapacity,
          preserveSlotCapacity: intervalBlocked,
          fallbackSlotCapacity: latestRestaurant?.accept_people_per_each_time
        },
        roomNames,
        token
      );

      const definitions = effectiveDraft.definitions;
      const settingsPayload = {
        each_time: effectiveDraft.slotInterval,
        timezone:
          definitions.timezone ||
          latestRestaurant?.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      if (!intervalBlocked) {
        settingsPayload.accept_people_per_each_time = Number(
          effectiveDraft.slotCapacity
        );
      }

      if (!blocked) {
        settingsPayload.limit_people_reservation = Number(
          definitions.limitPeopleReservation
        );
        settingsPayload.auto_accept_limit = Number(definitions.autoAcceptLimit);
        settingsPayload.only_auto_accept_before_this_time = `${String(
          Number(definitions.autoAcceptMinHours) || 0
        ).padStart(2, "0")}:00`;
        settingsPayload.only_auto_accept_before_this_time_future =
          Number(definitions.autoAcceptFutureDays) * 24;
      }

      if (!legacyDefinitionsBlocked) {
        settingsPayload.number_of_babychairs = Number(definitions.babyChairs);
        settingsPayload.limit_baby_chairs_reservation = Number(
          definitions.babyChairsPerReservation
        );
      }

      const updatedRestaurant = await restaurantSetupApi.patchRestaurant(
        restaurantId,
        settingsPayload,
        token
      );

      await restaurantSetupApi.ensureAverageStay(
        restaurantId,
        effectiveDraft.averageStayRules,
        token
      );

      await restaurantSetupApi.ensureNotifications(
        restaurantId,
        effectiveDraft,
        updatedRestaurant?.restaurant_name || latestRestaurant?.restaurant_name,
        token
      );

      await Promise.all([
        dispatch(fetchRestaurantDetails(restaurantId)),
        dispatch(fetchPlaceOfTable(restaurantId)),
        dispatch(fetchTables(restaurantId)),
        dispatch(fetchRestaurantOpenDays(restaurantId)),
        dispatch(fetchEstimatedTimeCustomersSpend(restaurantId)),
        dispatch(getRestaurantNotificationsList(restaurantId))
      ]);

      startRestaurantReservationTour(restaurantId);
    } catch (saveError) {
      console.error("Restaurant setup wizard save failed:", saveError);
      setError(
        t("ui.restaurantSetupWizard.errors.save", {
          error: getSetupApiErrorMessage(saveError)
        })
      );
      setIsSaving(false);
    }
  };

  const renderDefinition = () => {
    const defs = draft.definitions;
    const title = t(`ui.restaurantSetupWizard.definitions.${currentDefinitionKey}.title`);
    const body = t(`ui.restaurantSetupWizard.definitions.${currentDefinitionKey}.body`);

    if (definitionIsPlanBlocked) {
      return (
        <>
          <Title>{title}</Title>
          <Description>{body}</Description>
          {currentDefinitionKey === "limitPeopleReservation" && (
            <DefinitionValueRow>
              <Stepper
                value=""
                onChange={() => {}}
                min={1}
                max={100}
                disabled
                displayEmpty
              />
              <DefinitionUnit>
                {t("ui.restaurantSetupWizard.definitionUnits.people")}
              </DefinitionUnit>
            </DefinitionValueRow>
          )}
          <InfoBox>{t("ui.restaurantSetupWizard.planLocked")}</InfoBox>
        </>
      );
    }

    const numberDefinition = (
      value,
      onChange,
      min = 0,
      max = 1000,
      unitKey = null
    ) => (
      <DefinitionValueRow>
        <Stepper value={value} onChange={onChange} min={min} max={max} />
        {unitKey && (
          <DefinitionUnit>
            {t(`ui.restaurantSetupWizard.definitionUnits.${unitKey}`)}
          </DefinitionUnit>
        )}
      </DefinitionValueRow>
    );

    if (currentDefinitionKey === "limitPeopleReservation") {
      return (
        <>
          <Title>{title}</Title>
          <Description>{body}</Description>
          {numberDefinition(
            defs.limitPeopleReservation,
            value => updateDefinition("limitPeopleReservation", value),
            1,
            100,
            "people"
          )}
        </>
      );
    }

    if (currentDefinitionKey === "autoAccept") {
      return (
        <>
          <Title>{title}</Title>
          <Description>{body}</Description>
          <StepperRuleGroup>
            <StepperRule>
              <span>{t("ui.restaurantSetupWizard.definitions.autoAccept.upTo")}</span>
              <Stepper
                value={defs.autoAcceptLimit}
                onChange={value => updateDefinition("autoAcceptLimit", value)}
                min={0}
                max={100}
              />
              <span>{t("ui.restaurantSetupWizard.definitionUnits.people")}</span>
            </StepperRule>
            <StepperRule>
              <span>{t("ui.restaurantSetupWizard.definitions.autoAccept.lessThan")}</span>
              <Stepper
                value={defs.autoAcceptFutureDays}
                onChange={value => updateDefinition("autoAcceptFutureDays", value)}
                min={0}
                max={365}
              />
              <span>{t("ui.restaurantSetupWizard.definitionUnits.days")}</span>
            </StepperRule>
            <StepperRule>
              <span>{t("ui.restaurantSetupWizard.definitions.autoAccept.moreThan")}</span>
              <Stepper
                value={defs.autoAcceptMinHours}
                onChange={value => updateDefinition("autoAcceptMinHours", value)}
                min={0}
                max={720}
              />
              <span>{t("ui.restaurantSetupWizard.definitionUnits.hours")}</span>
            </StepperRule>
          </StepperRuleGroup>
        </>
      );
    }

    if (currentDefinitionKey === "babyChairs") {
      return (
        <>
          <Title>{title}</Title>
          <Description>{body}</Description>
          <AverageStayRule>
            <span>{t("ui.restaurantSetupWizard.definitions.babyChairs.weHave")}</span>
            <Stepper
              value={defs.babyChairs}
              onChange={value => updateDefinition("babyChairs", value)}
              min={0}
              max={100}
            />
            <span>{t("ui.restaurantSetupWizard.definitions.babyChairs.andAllow")}</span>
            <Stepper
              value={defs.babyChairsPerReservation}
              onChange={value => updateDefinition("babyChairsPerReservation", value)}
              min={0}
              max={20}
            />
            <span>{t("ui.restaurantSetupWizard.definitions.babyChairs.perReservation")}</span>
          </AverageStayRule>
        </>
      );
    }

    if (currentDefinitionKey === "timezone") {
      return (
        <>
          <Title>{title}</Title>
          <Description>{body}</Description>
          <Select
            value={defs.timezone || ""}
            onChange={event => updateDefinition("timezone", event.target.value)}
          >
            {timezones.map(timezone => (
              <option key={timezone} value={timezone}>
                {timezone}
              </option>
            ))}
          </Select>
        </>
      );
    }

    return null;
  };

  const renderPage = () => {
    if (isSaving) {
      return (
        <SavingBox>
          <Spinner />
          <Title>{t("ui.restaurantSetupWizard.saving.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.saving.body")}</Description>
          {error && <ErrorBox>{error}</ErrorBox>}
        </SavingBox>
      );
    }

    if (pageType === "roomCount") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.rooms.count.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.rooms.count.body")}</Description>
          <Stepper value={draft.roomCount} onChange={setRoomCount} min={1} max={MAX_ROOMS} />
        </>
      );
    }

    if (pageType === "roomNames") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.rooms.names.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.rooms.names.body")}</Description>
          {draft.rooms.map((room, index) => (
            <RoomNameRow key={room._key || index}>
              <RoomLabel>
                {t("ui.restaurantSetupWizard.rooms.names.room", { number: index + 1 })}
              </RoomLabel>
              <Input
                type="text"
                value={room.name}
                placeholder={t("ui.restaurantSetupWizard.rooms.names.placeholder", {
                  number: index + 1
                })}
                onChange={event => setRoomName(index, event.target.value)}
              />
            </RoomNameRow>
          ))}
        </>
      );
    }

    if (pageType === "roomTables") {
      const room = draft.rooms[pageIndex];
      return (
        <>
          <Title>
            {t("ui.restaurantSetupWizard.tables.title", {
              room: room?.name || pageIndex + 1
            })}
          </Title>
          <Description>{t("ui.restaurantSetupWizard.tables.body")}</Description>
          <Grid>
            <Field>
              <FieldLabel>{t("ui.restaurantSetupWizard.tables.count")}</FieldLabel>
              <Stepper
                value={room.tables.length}
                onChange={value => setRoomTablesCount(pageIndex, value)}
                min={1}
                max={MAX_TABLES_PER_ROOM}
              />
            </Field>
            <Field>
              <FieldLabel>{t("ui.restaurantSetupWizard.tables.defaultSeats")}</FieldLabel>
              <Stepper
                value={room.defaultSeats}
                onChange={value => setRoomDefaultSeats(pageIndex, value)}
                min={1}
                max={100}
              />
            </Field>
          </Grid>
          <FieldLabel>{t("ui.restaurantSetupWizard.tables.review")}</FieldLabel>
          <TablesScroll>
            {room.tables.map((table, tableIndex) => (
              <TableRow key={tableIndex}>
                <div>
                  <strong>
                    {t("ui.restaurantSetupWizard.tables.table", {
                      number: tableIndex + 1
                    })}
                  </strong>
                  <div style={{ fontSize: "0.76rem", color: "#8a7f81" }}>
                    {t("ui.restaurantSetupWizard.tables.seats")}
                  </div>
                </div>
                <Stepper
                  value={table.seats}
                  onChange={value => setTableSeats(pageIndex, tableIndex, value)}
                  min={1}
                  max={100}
                />
              </TableRow>
            ))}
          </TablesScroll>
        </>
      );
    }

    if (pageType === "schedule") {
      const schedule = draft.scheduleDraft || createDefaultRestaurantSetupDraft().scheduleDraft;
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.schedule.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.body")}</Description>
          <ScheduleBuilder>
            <Weekdays>
              {WEEKDAYS.map(day => (
                <Weekday
                  key={day}
                  type="button"
                  $selected={schedule.days.includes(day)}
                  onClick={() => toggleScheduleDraftDay(day)}
                >
                  {t(`ui.restaurantSetupWizard.weekdaysShort.${day}`)}
                </Weekday>
              ))}
            </Weekdays>
            <Grid>
              <Field style={{ marginBottom: 0 }}>
                <FieldLabel>{t("ui.restaurantSetupWizard.schedule.time.start")}</FieldLabel>
                <Input
                  type="time"
                  value={schedule.startTime}
                  onChange={event => updateScheduleDraft({ startTime: event.target.value })}
                />
              </Field>
              <Field style={{ marginBottom: 0 }}>
                <FieldLabel>{t("ui.restaurantSetupWizard.schedule.time.end")}</FieldLabel>
                <Input
                  type="time"
                  value={schedule.endTime}
                  onChange={event => updateScheduleDraft({ endTime: event.target.value })}
                />
              </Field>
            </Grid>
            <AddScheduleButton type="button" onClick={saveScheduleRow}>
              <AddIcon aria-hidden="true">+</AddIcon>
              {t("ui.restaurantSetupWizard.schedule.save")}
            </AddScheduleButton>
          </ScheduleBuilder>

          {draft.schedules?.length > 0 && (
            <>
              <FieldLabel>{t("ui.restaurantSetupWizard.schedule.saved")}</FieldLabel>
              <SummaryList>
                {draft.schedules.map((savedSchedule, index) => (
                  <SummaryCard key={`${savedSchedule.startTime}-${savedSchedule.endTime}-${index}`}>
                    <div>
                      <strong>
                        {savedSchedule.days
                          .map(day => t(`ui.restaurantSetupWizard.weekdays.${day}`))
                          .join(", ")}
                      </strong>
                      <div>{savedSchedule.startTime} – {savedSchedule.endTime}</div>
                    </div>
                    <DeleteScheduleButton
                      type="button"
                      onClick={() => deleteScheduleRow(index)}
                      aria-label={t("ui.restaurantSetupWizard.schedule.delete")}
                      title={t("ui.restaurantSetupWizard.schedule.delete")}
                    >
                      ×
                    </DeleteScheduleButton>
                  </SummaryCard>
                ))}
              </SummaryList>
            </>
          )}
        </>
      );
    }

    if (pageType === "slotInterval") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.schedule.interval.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.interval.body")}</Description>
          <ChoiceGrid>
            {["00:15", "00:30", "00:45", "01:00"].map(value => (
              <Choice
                type="button"
                key={value}
                $selected={draft.slotInterval === value}
                onClick={() => updateDraft({ ...draft, slotInterval: value })}
              >
                {t("ui.restaurantSetupWizard.minutesValue", {
                  minutes: intervalMinutes(value)
                })}
              </Choice>
            ))}
          </ChoiceGrid>
        </>
      );
    }

    if (pageType === "slotCapacity") {
      const selectedIntervalMinutes = intervalMinutes(draft.slotInterval);
      return (
        <>
          <Title>
            {t("ui.restaurantSetupWizard.schedule.capacity.title", {
              minutes: selectedIntervalMinutes
            })}
          </Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.capacity.body")}</Description>
          <Stepper
            value={slotCapacityIsPlanBlocked ? "" : draft.slotCapacity}
            onChange={
              slotCapacityIsPlanBlocked
                ? () => {}
                : value => updateDraft({ ...draft, slotCapacity: value })
            }
            min={1}
            max={1000}
            disabled={slotCapacityIsPlanBlocked}
            displayEmpty={slotCapacityIsPlanBlocked}
          />
          {slotCapacityIsPlanBlocked && (
            <InfoBox>{t("ui.restaurantSetupWizard.planLocked")}</InfoBox>
          )}
        </>
      );
    }

    if (pageType === "averageStay") {
      const currentAverageStay =
        draft.averageStayDraft || createDefaultRestaurantSetupDraft().averageStayDraft;
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.averageStay.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.averageStay.body")}</Description>

          <ScheduleBuilder>
            <AverageStayBuilderRow>
              <AverageStayBuilderField>
                <FieldLabel>{t("ui.restaurantSetupWizard.averageStay.people")}</FieldLabel>
                <Stepper
                  value={currentAverageStay.people}
                  onChange={value => updateAverageStayDraft({ people: value })}
                  min={1}
                  max={100}
                />
              </AverageStayBuilderField>
              <AverageStayBuilderField>
                <FieldLabel>{t("ui.restaurantSetupWizard.averageStay.duration")}</FieldLabel>
                <DurationStepper
                  value={currentAverageStay.minutes}
                  onChange={value => updateAverageStayDraft({ minutes: value })}
                  t={t}
                />
              </AverageStayBuilderField>
            </AverageStayBuilderRow>

            <AddScheduleButton type="button" onClick={saveAverageStayRule}>
              <AddIcon>+</AddIcon>
              {t("ui.restaurantSetupWizard.averageStay.save")}
            </AddScheduleButton>
          </ScheduleBuilder>

          {draft.averageStayRules?.length > 0 && (
            <>
              <FieldLabel>{t("ui.restaurantSetupWizard.averageStay.saved")}</FieldLabel>
              <SummaryList>
                {[...draft.averageStayRules]
                  .sort((a, b) => Number(a.people) - Number(b.people))
                  .map(rule => (
                    <SummaryCard key={rule.people}>
                      <span>
                        {t("ui.restaurantSetupWizard.averageStay.rule", {
                          people: rule.people,
                          duration: durationLabel(rule.minutes, t)
                        })}
                      </span>
                      <DeleteScheduleButton
                        type="button"
                        onClick={() => deleteAverageStayRule(rule.people)}
                        aria-label={t("ui.restaurantSetupWizard.averageStay.delete")}
                        title={t("ui.restaurantSetupWizard.averageStay.delete")}
                      >
                        ×
                      </DeleteScheduleButton>
                    </SummaryCard>
                  ))}
              </SummaryList>
            </>
          )}

          <InfoBox>{t("ui.restaurantSetupWizard.averageStay.note")}</InfoBox>
        </>
      );
    }

    if (pageType === "definition") {
      return renderDefinition();
    }

    if (pageType === "notifications") {
      const phone = draft.phoneNotifications;
      const email = draft.emailNotifications;
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.notifications.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.notifications.body")}</Description>

          <NotificationSection>
            <NotificationTitle>{t("ui.restaurantSetupWizard.notifications.phone.title")}</NotificationTitle>
            <NotificationDescription>
              {t("ui.restaurantSetupWizard.notifications.phone.body")}
            </NotificationDescription>
            <ToggleRow style={{ marginBottom: phone.enabled ? "14px" : 0 }}>
              <Toggle
                type="button"
                $on={phone.enabled}
                onClick={() =>
                  updateDraft(current => ({
                    ...current,
                    phoneNotifications: {
                      ...current.phoneNotifications,
                      enabled: !current.phoneNotifications.enabled
                    }
                  }))
                }
              />
              <strong>
                {phone.enabled
                  ? t("ui.restaurantSetupWizard.yes")
                  : t("ui.restaurantSetupWizard.no")}
              </strong>
            </ToggleRow>
            {phone.enabled && (
              <>
                <PhoneField
                  countryCodeNumber={phone.countryCode}
                  setCountryCodeNumber={value =>
                    updateDraft(current => ({
                      ...current,
                      phoneNotifications: {
                        ...current.phoneNotifications,
                        countryCode: value
                      }
                    }))
                  }
                  phoneWithoutCountryCode={phone.number}
                  setPhoneWithoutCountryCode={value =>
                    updateDraft(current => ({
                      ...current,
                      phoneNotifications: {
                        ...current.phoneNotifications,
                        number: value
                      }
                    }))
                  }
                  isPhoneValid={isPhoneValid}
                  setIsPhoneValid={setIsPhoneValid}
                  initialFullPhone={
                    phone.number ? `+${phone.countryCode}${phone.number}` : undefined
                  }
                />
                <InfoBox>{t("ui.restaurantSetupWizard.notifications.phone.note")}</InfoBox>
              </>
            )}
          </NotificationSection>

          <NotificationSection>
            <NotificationTitle>{t("ui.restaurantSetupWizard.notifications.email.title")}</NotificationTitle>
            <NotificationDescription>
              {t("ui.restaurantSetupWizard.notifications.email.body")}
            </NotificationDescription>
            <ToggleRow style={{ marginBottom: email.enabled ? "14px" : 0 }}>
              <Toggle
                type="button"
                $on={email.enabled}
                onClick={() =>
                  updateDraft(current => ({
                    ...current,
                    emailNotifications: {
                      ...current.emailNotifications,
                      enabled: !current.emailNotifications.enabled
                    }
                  }))
                }
              />
              <strong>
                {email.enabled
                  ? t("ui.restaurantSetupWizard.yes")
                  : t("ui.restaurantSetupWizard.no")}
              </strong>
            </ToggleRow>
            {email.enabled && (
              <>
                <Input
                  type="email"
                  value={email.email}
                  placeholder={t("ui.restaurantSetupWizard.notifications.email.placeholder")}
                  onChange={event =>
                    updateDraft(current => ({
                      ...current,
                      emailNotifications: {
                        ...current.emailNotifications,
                        email: event.target.value
                      }
                    }))
                  }
                />
                <InfoBox>{t("ui.restaurantSetupWizard.notifications.email.note")}</InfoBox>
              </>
            )}
          </NotificationSection>
        </>
      );
    }

    return null;
  };

  const previousPage = getPreviousPage();
  const isFinalInputPage = pageType === "notifications";

  return (
    <Overlay role="presentation">
      <Modal role="dialog" aria-modal="true" aria-label={t("ui.restaurantSetupWizard.ariaLabel")}>
        <Header>
          <Eyebrow>{t("ui.restaurantSetupWizard.setupTitle")}</Eyebrow>
          <Progress>
            {["rooms", "schedule", "definitions", "notifications"].map((section, index) => (
              <ProgressItem key={section} $active={sectionIndex === index} $done={sectionIndex > index}>
                {t(`ui.restaurantSetupWizard.sections.${section}`)}
              </ProgressItem>
            ))}
          </Progress>
        </Header>

        <Body>
          {renderPage()}
          {error && !isSaving && <ErrorBox>{error}</ErrorBox>}
        </Body>

        {!isSaving && (
          <Footer>
            <BackButton type="button" onClick={handleBack} disabled={!previousPage}>
              {t("ui.restaurantSetupWizard.back")}
            </BackButton>

            {pageType === "notifications" && (
              <TextButton
                type="button"
                onClick={() => {
                  const nextDraft = {
                    ...draft,
                    phoneNotifications: {
                      ...draft.phoneNotifications,
                      enabled: false,
                      number: ""
                    },
                    emailNotifications: {
                      ...draft.emailNotifications,
                      enabled: false,
                      email: ""
                    }
                  };
                  updateDraft(nextDraft);
                  saveRestaurantSetupTourDraft(restaurantId, nextDraft);
                  saveAll(nextDraft, true);
                }}
              >
                {t("ui.restaurantSetupWizard.skipNotifications")}
              </TextButton>
            )}

            <NextButton type="button" onClick={isFinalInputPage ? () => saveAll() : handleNext}>
              {isFinalInputPage
                ? t("ui.restaurantSetupWizard.saveAndContinue")
                : t("ui.restaurantSetupWizard.next")}
            </NextButton>
          </Footer>
        )}
      </Modal>
    </Overlay>
  );
};

export default RestaurantSetupWizard;
